"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Calendar, CalendarRange, Zap, Check, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
const rentalPlans = [{
  id: "short-term",
  name: "Mobile Refrigeration Rental",
  iconName: "Calendar",
  durationTag: "Quote-Based Rental",
  summary: "Mobile refrigerated storage using the 6×8, 7×12, and 7×16 trailers for temporary food, beverage, event, and commercial cold-storage needs.",
  useCaseLabel: "Common use-cases",
  useCase1: "Food and beverage storage",
  useCase2: "Catering and food service",
  useCase3: "Farmers markets and mobile vendors",
  useCase4: "Events and special events",
  badgeText: "Pricing available upon request",
  isPriority: false
}, {
  id: "extended",
  name: "Mobile Freezer Rental",
  iconName: "CalendarRange",
  durationTag: "Pricing Provided With Quote",
  summary: "Freezer trailer options are available in the 6×8, 7×12, and 7×16 sizes. Availability, intended use, and operating requirements are confirmed with each quote.",
  useCaseLabel: "Common use-cases",
  useCase1: "Frozen food storage",
  useCase2: "Temporary cold storage",
  useCase3: "Commercial overflow storage",
  useCase4: "Events and catering operations",
  badgeText: "PRICING\xA0 PROVIDED WITH QUOTE",
  isPriority: true
}, {
  id: "emergency",
  name: "Power & Storage Add-Ons",
  iconName: "Zap",
  durationTag: "Request a Quote",
  summary: "Optional Honda 3000 generators and interior shelving may be available for the 6×8, 7×12, and 7×16 trailers. Pricing and availability are confirmed with each quote. Power, fuel, and optional-equipment requirements for the 53-foot reefer are confirmed individually.",
  useCaseLabel: "AVAILABLE OPTIONS",
  useCase1: "AVAILABLE OPTIONS",
  useCase2: "Honda 3000 generator for eligible smaller trailers",
  useCase3: "Interior shelving for eligible smaller trailers",
  useCase4: "53-foot reefer requirements confirmed individually",
  badgeText: "OPTIONS PROVIDED WITH\xA0 QUOTE",
  isPriority: false
}, {
  id: "fifty-three-foot",
  name: "53-Foot Reefer Trailer Rental",
  iconName: "Zap",
  durationTag: "Pricing Provided With Quote",
  summary: "Large-capacity temporary refrigerated storage for commercial needs. Availability, pricing, specifications, delivery requirements, intended use, and temperature requirements are confirmed individually.",
  useCaseLabel: "QUOTE CONSIDERATIONS",
  useCase1: "Rental dates and delivery location",
  useCase2: "Intended use",
  useCase3: "Temperature requirements",
  useCase4: "Site access and delivery requirements",
  badgeText: "Pricing available upon request",
  isPriority: false
}];
export default function RentalOptions() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="rental-options" data-nav="light" className="relative bg-background py-14 text-foreground md:py-20 lg:py-24">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-accent-foreground/20 bg-accent px-3.5 py-1 text-xs font-semibold text-accent-foreground">
            <span className="h-1.5 w-1.5 rounded-full bg-accent-foreground" />
            Rental Terms
          </div>
          
          <h1 className="mt-4 font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
            Rental options tailored to your schedule
          </h1>
          
          <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
            Choose from mobile refrigerated and freezer trailers, optional equipment, and a 53-foot reefer trailer. Pricing, availability, rental terms, delivery requirements, and equipment suitability are confirmed with each quote.
          </p>
        </div>

        {/* Pricing & Tier Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:mt-16 lg:gap-8">
          {rentalPlans.map((plan, i) => <motion.div key={plan.id} data-index={i} variants={itemVariants} className="flex h-full">
              <Card className={`relative flex h-full w-full flex-col justify-between rounded-lg border bg-card p-6 shadow-sm transition-[transform,border-color,box-shadow] duration-200 hover:-translate-y-1 hover:border-primary/50 hover:shadow-md sm:p-8 ${plan.isPriority ? "border-primary/40 ring-1 ring-primary/20" : "border-border"}`}>
                <div>
                  {/* Top Header info */}
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-muted text-foreground">
                      {plan.iconName === "Calendar" && <SafeHydrate><Calendar className="h-5 w-5 text-primary" /></SafeHydrate>}
                      {plan.iconName === "CalendarRange" && <CalendarRange className="h-5 w-5 text-primary" />}
                      {plan.iconName === "Zap" && <Zap className="h-5 w-5 text-chart-1" />}
                    </div>
                    <span className="rounded bg-muted px-2.5 py-1 text-right text-xs font-medium leading-tight text-foreground break-words">
                      {plan.durationTag}
                    </span>
                  </div>

                  <div className="mt-5">
                    <h2 className="font-default text-xl font-bold tracking-tight text-foreground sm:text-2xl">
                      {plan.name}
                    </h2>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                      {plan.summary}
                    </p>
                  </div>

                  {/* Informational Badge with exact copy */}
                  <div className="mt-5 rounded-md border border-border bg-muted/60 px-3.5 py-2.5 text-center">
                    <Badge variant="outline" className="border-border bg-background px-2.5 py-0.5 text-center text-xs font-semibold text-foreground uppercase tracking-wide whitespace-normal break-words">
                      {plan.badgeText}
                    </Badge>
                  </div>

                  {/* Divider */}
                  <div className="my-6 border-t border-border" />

                  {/* Duration Use Cases List */}
                  <div>
                    <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                      {plan.useCaseLabel || "Common use-cases"}
                    </span>
                    <ul className="mt-3 space-y-2.5 text-sm">
                      <li className="flex items-start gap-2.5 text-foreground">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-accent-foreground" />
                        <span className="break-words">{plan.useCase1}</span>
                      </li>
                      <li className="flex items-start gap-2.5 text-foreground">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-accent-foreground" />
                        <span className="break-words">{plan.useCase2}</span>
                      </li>
                      <li className="flex items-start gap-2.5 text-foreground">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-accent-foreground" />
                        <span className="break-words">{plan.useCase3}</span>
                      </li>
                      <li className="flex items-start gap-2.5 text-foreground">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-accent-foreground" />
                        <span className="break-words">{plan.useCase4}</span>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Footer CTA */}
                <div className={`mt-8 pt-2 ${plan.id === "fifty-three-foot" ? "space-y-2.5" : ""}`}>
                  <Button asChild variant="outline" className="w-full justify-center border-border font-semibold text-foreground transition-colors hover:border-primary hover:bg-primary hover:text-primary-foreground" size="lg">
                    <Link to="/ContactRequestAQuotePage" className="inline-flex items-center gap-2">
                      <span>Request a Quote</span>
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </Button>

                  {plan.id === "fifty-three-foot" && <Button asChild variant="link" className="w-full justify-center text-sm font-semibold text-primary">
                      <Link to="/FootRefrigeratedTrailerRentalsPage">
                        View the 53-foot reefer trailer
                      </Link>
                    </Button>}
                </div>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Assistance Banner */}
        <div className="mt-12 rounded-lg border border-border bg-card p-6 shadow-xs sm:p-8">
          <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
            <div className="space-y-1">
              <h3 className="font-default text-lg font-bold text-foreground">
                Need cold storage for your operation?
              </h3>
              <p className="text-sm text-muted-foreground">
                Availability and rental terms are provided with your quote for Southern California and our listed Las Vegas-area communities.
              </p>
            </div>
            <div className="flex w-full flex-wrap items-center gap-3 sm:w-auto">
              <a href="tel:2135218821" className="inline-flex h-9 items-center gap-2 rounded-md bg-secondary px-4 py-2 text-sm font-medium text-secondary-foreground">
                213-521-8821
              </a>
              <Button asChild variant="default">
                <Link to="/ContactRequestAQuotePage">
                  Request a Quote
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}